<script setup>
import { CheckCircleIcon } from '@heroicons/vue/24/outline/index';
import {
  computed, onMounted, ref, toValue, watch,
} from 'vue';
import {
  find, pipe, prop, propEq,
} from 'ramda';
import {RouterLink, useRoute, useRouter} from 'vue-router';
import { useI18n } from 'vue-i18n';
import { isIndividualPlan, isNotNullAndNotEmpty } from '@/utilities';
import useCallEndPointWith from '@/services/backend/useCallEndPointWith';
import InPlaceAlert from '@/components/InPlaceAlert.vue';
import RedeemKeyComponent from '@/components/register/RedeemKeyComponent.vue';
import PricingComponent from '@/components/register/PricingComponent.vue';
import Price from '@/components/Price.vue';
import Trans from '@/i18n/translation';
import WithLoader from '@/components/WithLoader.vue';
import {trackEvent} from "@/services/analytics/googleAnalytics.js";

const route = useRoute();
const router = useRouter();
const emit = defineEmits(['paymentSuccess', 'back', 'freePaymentSuccess']);

onMounted(() => {
  trackEvent('step_billing', {
    event_category: ['Payments', 'Register'],
    event_label: 'Billing Component Loaded',
  });
});

const payPalResponseMessage = ref({
  message: '',
  isError: false,
});

const props = defineProps({
  chosenPlan: {
    required: true,
  },
  vat: {
    required: true,
  },
  emailForPayment: {
    required: true,
  },
  paypalShape: {
    default: 'pill',
    type: String,
  },
  paypalLayout: {
    default: 'horizontal',
    type: String,
  },
});
const { t } = useI18n({ messages: props.chosenPlan.description });
const hasError = payPalResponseMessage.value.isError;
const appliedDiscount = ref({});
const numSeatsToBuy = ref(1);
const minNumSeats = 1;
const maxNumSeats = 9999;
const updatedTotalPrice = ref(props.chosenPlan.subscriptionPrice);

const login = Trans.i18nRoute({ name: 'login' });

const isFreePlan = computed(() => updatedTotalPrice.value === 0);

const handlePriceUpdate = (event) => {
  updatedTotalPrice.value = event.totalPrice;
};
sessionStorage.setItem('chosenPlan', JSON.stringify(t('planName')));

watch(numSeatsToBuy, async (newNum) => {
  if (newNum > maxNumSeats) {
    numSeatsToBuy.value = maxNumSeats;
  }
  if (newNum < minNumSeats) {
    numSeatsToBuy.value = minNumSeats;
  }
});

const {
  callEndpoint, endpointData, userFriendlyError, isLoading,
} = useCallEndPointWith('createSubscription');

async function callCreateSubscription() {
  await callEndpoint({
    method: 'POST',
    request: {
      planId: props.chosenPlan.planId,
      email: props.emailForPayment,
      seats: numSeatsToBuy.value,
      vat: props.vat,
      redeemKey: prop('key', toValue(appliedDiscount)),
      returnPath: router.resolve(
        Trans.i18nRoute({ name: 'register-payment-return', params: { success: true } }),
      ).path,
      cancelPath: router.resolve(
        Trans.i18nRoute({ name: 'register-payment-return', params: { success: false } }),
      ).path,
    },
  });

  const response = endpointData.value;

  if (isNotNullAndNotEmpty(userFriendlyError.value)) {
    payPalResponseMessage.value = {
      message: 'Error creating your subscription',
      isError: true,
    };
    return;
  }
  const approveUrl = pipe(prop('links'), find(propEq('approve', 'rel')), prop('href'))(response);

  window.open(approveUrl, '_self');
}
const onFreePlanClick = () => {
  emit('freePaymentSuccess', true);
  callCreateSubscription();
};
console.log('vat in billing', props.vat);
const showMessage = computed(() => isNotNullAndNotEmpty(payPalResponseMessage.value.message));
</script>

<template>
  <div
    class="p-12 pt-8 bg-basecolor rounded-md text-primary lg:col-start-2 lg:row-start-1 lg:mx-auto lg:w-full lg:max-w-lg"
  >
    <div class="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
      <InPlaceAlert
        :message="userFriendlyError"
        type="error"
        @close-alert="userFriendlyError = ''"
      />
    </div>
    <div class="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
      <h2 class="text-xl">
        {{ t('planName') }}
      </h2>

      <div
        id="individual-plan-pricing"
        v-if="isIndividualPlan(chosenPlan)"
      >
        <dl class="space-y-6 border-opacity-10 pt-6 font-medium">
          <div class="flex items-center justify-between">
            <dt>{{ $t('registerView.billing.yearlyPriceLabel') }}</dt>
            <dd>
              <Price :price="chosenPlan.subscriptionPrice" />
            </dd>
          </div>
        </dl>
      </div>

      <div
        id="company-plan-pricing"
        v-else
      >
        <dl class="space-y-6 border-opacity-10 pt-6 font-medium">
          <div class="flex items-center justify-between">
            <dt>{{ $t('registerView.billing.yearlyPriceLabel') }}</dt>
            <dd class="text-lg">
              <Price :price="chosenPlan.seatPrice" />
            </dd>
          </div>

          <div class="flex items-center justify-between">
            <dt>{{ $t('registerView.billing.numLicensesLabel') }}</dt>
            <dd class="text-lg w-[30%]">
              <input
                type="number"
                v-model="numSeatsToBuy"
                class="block input-transparent-text w-full text-right"
                :required="true"
                :min="minNumSeats"
                :max="maxNumSeats"
              >
            </dd>
          </div>
        </dl>
      </div>

      <PricingComponent
        :applied-discount="appliedDiscount"
        :vat="vat"
        :number-seats="numSeatsToBuy"
        :chosen-plan="chosenPlan"
        @price-updated="handlePriceUpdate"
      />

      <RedeemKeyComponent v-model="appliedDiscount" />

      <div class="mt-12 mb-12">
        <p class>
          {{ $t('registerView.billing.terms.desc') }}
        </p>

        <ul
          role="list"
          class="mt-8 space-y-3 leading-6 text-primary"
        >
          <li class="flex gap-x-3">
            <CheckCircleIcon
              class="h-6 w-5 flex-none"
              aria-hidden="true"
            />
            {{ $t('registerView.billing.terms.bullet1') }}
          </li>
          <li class="flex gap-x-3">
            <CheckCircleIcon
              class="h-6 w-5 flex-none"
              aria-hidden="true"
            />
            {{ $t('registerView.billing.terms.bullet2') }}
          </li>
          <li class="flex gap-x-3">
            <CheckCircleIcon
              class="h-6 w-5 flex-none"
              aria-hidden="true"
            />
            {{ $t('registerView.billing.terms.bullet3') }}
          </li>
        </ul>
      </div>

      <div
        class="mt-10 flex justify-center pt-6"
        id="paypal-button-container"
      >
        <WithLoader :is-loading="isLoading">
          <button
            v-if="isFreePlan"
            @click="onFreePlanClick"
            class="rounded-md border border-transparent bg-secondary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-50"
          >
            {{ $t('registerView.billing.free') }}
          </button>
          <button
            v-else
            @click="callCreateSubscription"
            class="rounded-md border border-transparent bg-secondary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-50"
          >
            {{ $t('registerView.billing.payWithPaypal') }}
          </button>
        </WithLoader>
      </div>
      <div
        class="p-5"
        :class="{ 'bg-red-200': hasError, 'bg-green-200': !hasError }"
        v-if="showMessage"
      >
        <span>{{ payPalResponseMessage.message }}</span>
      </div>
    </div>
  </div>
</template>
